import { useState, useMemo } from 'react';
import axios from 'axios';

const API = '/api';

function BulkEdit({ transactions, onSaved }) {
  const [filters, setFilters] = useState({ description: '', category: '', subCategory: '', type: '' });
  const [updates, setUpdates] = useState({ category: '', subCategory: '', type: '' });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const updateFilter = (key, val) => setFilters(prev => ({ ...prev, [key]: val }));
  const updateUpdate = (key, val) => setUpdates(prev => ({ ...prev, [key]: val }));

  const matched = useMemo(() => {
    return transactions.filter(t => {
      return (
        (!filters.description || (t.description || '').toLowerCase().includes(filters.description.toLowerCase())) &&
        (!filters.category || (t.category || '').toLowerCase().includes(filters.category.toLowerCase())) &&
        (!filters.subCategory || (t.subCategory || '').toLowerCase().includes(filters.subCategory.toLowerCase())) &&
        (!filters.type || t.type === filters.type)
      );
    });
  }, [transactions, filters]);

  const hasFilters = Object.values(filters).some(v => v !== '');
  const hasUpdates = Object.values(updates).some(v => v !== '');

  const handleApply = async () => {
    if (!hasUpdates || matched.length === 0) return;
    setLoading(true);
    setStatus('');
    try {
      const payload = {};
      if (updates.category) payload.category = updates.category;
      if (updates.subCategory) payload.subCategory = updates.subCategory;
      if (updates.type) payload.type = updates.type;

      await Promise.all(
        matched.map(t => axios.put(`${API}/transactions/${t.id}`, { ...t, ...payload }))
      );

      setStatus(`✓ Updated ${matched.length} transactions`);
      setConfirmed(false);
      onSaved();
    } catch (err) {
      setStatus('Error: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    padding: '8px 10px',
    border: '1px solid var(--border)',
    borderRadius: '4px',
    background: 'var(--bg)',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '13px',
    color: 'var(--text)',
    outline: 'none',
    width: '100%'
  };

  const labelStyle = {
    fontFamily: 'DM Mono, monospace',
    fontSize: '11px',
    textTransform: 'uppercase',
    color: 'var(--muted)',
    letterSpacing: '0.5px',
    marginBottom: '5px'
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

      {/* Filter section */}
      <div className="form-wrap">
        <div className="form-title">Search Transactions</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Description contains</label>
            <input
              type="text"
              value={filters.description}
              onChange={e => updateFilter('description', e.target.value)}
              placeholder="e.g. ice cream"
              style={inputStyle}
            />
          </div>
          <div className="form-group">
            <label>Category contains</label>
            <input
              type="text"
              value={filters.category}
              onChange={e => updateFilter('category', e.target.value)}
              placeholder="e.g. food"
            />
          </div>
          <div className="form-group">
            <label>Sub Category contains</label>
            <input
              type="text"
              value={filters.subCategory}
              onChange={e => updateFilter('subCategory', e.target.value)}
              placeholder="e.g. dining"
            />
          </div>
          <div className="form-group">
            <label>Type</label>
            <select value={filters.type} onChange={e => updateFilter('type', e.target.value)}>
              <option value="">Any</option>
              <option value="expense">Expense</option>
              <option value="income">Income</option>
              <option value="transfer">Transfer</option>
            </select>
          </div>
        </div>

        {/* Match count */}
        <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', color: hasFilters ? (matched.length > 0 ? 'var(--income)' : 'var(--expense)') : 'var(--muted)', marginTop: '8px' }}>
          {hasFilters ? `${matched.length} transactions matched` : 'Enter search criteria above'}
        </div>
      </div>

      {/* Matched preview */}
      {hasFilters && matched.length > 0 && (
        <div className="table-wrap">
          <div className="table-header">
            <div className="table-title">Matched Transactions ({matched.length})</div>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th>Sub Category</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {matched.map(t => (
                  <tr key={t.id}>
                    <td className="td-date">{new Date(t.date).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                    <td style={{ fontSize: '13px' }}>{t.description}</td>
                    <td><span className={`badge badge-${t.type}`}>{t.type}</span></td>
                    <td className="td-mono" style={{ color: 'var(--muted)', fontSize: '12px' }}>{t.category || '—'}</td>
                    <td className="td-mono" style={{ color: 'var(--muted)', fontSize: '12px' }}>{t.subCategory || '—'}</td>
                    <td style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', color: Number(t.amount) < 0 ? 'var(--expense)' : 'var(--income)' }}>
                      {Number(t.amount) < 0 ? '−' : '+'}${Math.abs(Number(t.amount)).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Update section */}
      {hasFilters && matched.length > 0 && (
        <div className="form-wrap">
          <div className="form-title">Apply Changes to {matched.length} Transactions</div>
          <div className="form-grid">
            <div className="form-group">
              <label>Set Category</label>
              <input
                type="text"
                value={updates.category}
                onChange={e => updateUpdate('category', e.target.value)}
                placeholder="leave blank to skip"
              />
            </div>
            <div className="form-group">
              <label>Set Sub Category</label>
              <input
                type="text"
                value={updates.subCategory}
                onChange={e => updateUpdate('subCategory', e.target.value)}
                placeholder="leave blank to skip"
              />
            </div>
            <div className="form-group">
              <label>Set Type</label>
              <select value={updates.type} onChange={e => updateUpdate('type', e.target.value)}>
                <option value="">leave as is</option>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
                <option value="transfer">Transfer</option>
              </select>
            </div>
          </div>

          {/* Confirm toggle */}
          {hasUpdates && (
            <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="confirm"
                checked={confirmed}
                onChange={e => setConfirmed(e.target.checked)}
              />
              <label htmlFor="confirm" style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', color: 'var(--muted)', cursor: 'pointer' }}>
                I confirm I want to update {matched.length} transactions
              </label>
            </div>
          )}

          <div className="form-actions" style={{ marginTop: '16px' }}>
            <button
              className="btn-primary"
              onClick={handleApply}
              disabled={!hasUpdates || !confirmed || loading}
              style={{ opacity: (!hasUpdates || !confirmed || loading) ? 0.5 : 1 }}
            >
              {loading ? 'Updating...' : `Apply to ${matched.length} Transactions`}
            </button>
            <button
              className="btn-cancel"
              onClick={() => {
                setFilters({ description: '', category: '', subCategory: '', type: '' });
                setUpdates({ category: '', subCategory: '', type: '' });
                setStatus('');
                setConfirmed(false);
              }}
            >
              Clear
            </button>
          </div>
          {status && (
            <div style={{ fontFamily: 'DM Mono, monospace', fontSize: '12px', color: status.startsWith('✓') ? 'var(--income)' : 'var(--expense)', marginTop: '10px' }}>
              {status}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BulkEdit;